import { getPreviewUrl } from '@/services/driveApi';
import { getFileIcon, getFileColor } from '@/mocks/driveFiles';
import type { DriveItem } from '@/mocks/driveFiles';

interface FileThumbnailProps {
  item: DriveItem;
  size?: 'sm' | 'md';
  className?: string;
}

// Mock preview thumbnails for demo image files (Stable Diffusion generated)
const mockImagePreviews: Record<string, string> = {
  '여행_사진_001.jpg':
    'https://readdy.ai/api/search-image?query=Beautiful%20travel%20landscape%20photography%2C%20vibrant%20saturated%20colors%2C%20mountain%20lake%20with%20clear%20blue%20sky%2C%20professional%20travel%20photo%2C%20sharp%20details%2C%20minimal%20editing%20style&width=300&height=300&seq=thumb-travel-001&orientation=squarish',
  '프로필_사진.png':
    'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%2C%20clean%20studio%20lighting%2C%20simple%20neutral%20background%2C%20warm%20natural%20expression%2C%20minimalist%20style%2C%20soft%20shadows%2C%20high%20quality&width=300&height=300&seq=thumb-profile-001&orientation=squarish',
  '제품_샘플.webp':
    'https://readdy.ai/api/search-image?query=Modern%20product%20photography%2C%20minimalist%20white%20background%2C%20elegant%20design%20object%2C%20clean%20studio%20lighting%2C%20soft%20shadows%2C%20commercial%20product%20shot%2C%20high%20detail&width=300&height=300&seq=thumb-product-001&orientation=squarish',
  '인테리어_참고.jpg':
    'https://readdy.ai/api/search-image?query=Modern%20interior%20design%20living%20room%2C%20warm%20wood%20tones%2C%20natural%20light%2C%20minimalist%20furniture%2C%20cozy%20aesthetic%2C%20architectural%20digest%20style%2C%20harmonious%20color%20palette&width=300&height=300&seq=thumb-interior-001&orientation=squarish',
  '로고_디자인_최종.png':
    'https://readdy.ai/api/search-image?query=Modern%20minimalist%20logo%20design%2C%20clean%20geometric%20shapes%2C%20white%20background%2C%20professional%20branding%2C%20flat%20design%20style%2C%20corporative%20identity%2C%20simple%20elegant&width=300&height=300&seq=thumb-logo-001&orientation=squarish',
  '스크린샷_20260806.png':
    'https://readdy.ai/api/search-image?query=Clean%20modern%20web%20dashboard%20interface%2C%20light%20mode%20UI%20design%2C%20data%20visualization%20with%20charts%2C%20minimalist%20admin%20panel%2C%20professional%20app%20screenshot%2C%20white%20background&width=300&height=300&seq=thumb-screenshot-001&orientation=squarish',
};

// Mock preview thumbnails for demo PDF files (Stable Diffusion generated)
const mockPdfPreviews: Record<string, string> = {
  '회의록_7월.pdf':
    'https://readdy.ai/api/search-image?query=Professional%20meeting%20minutes%20document%20preview%2C%20clean%20white%20page%20with%20organized%20text%20layout%2C%20blue%20header%20with%20title%2C%20structured%20bullet%20points%20and%20tables%2C%20corporate%20document%20style%2C%20minimal%20design%2C%20crisp%20typography%2C%20soft%20natural%20lighting%20on%20paper%2C%20high%20angle%20view%2C%20realistic%20document%20photography&width=300&height=300&seq=thumb-pdf-meeting-001&orientation=squarish',
  '계약서_초안.pdf':
    'https://readdy.ai/api/search-image?query=Formal%20contract%20document%20draft%20preview%2C%20white%20paper%20with%20dense%20legal%20text%20paragraphs%2C%20signature%20lines%20at%20bottom%2C%20official%20document%20header%2C%20professional%20legal%20format%2C%20clean%20structured%20layout%2C%20soft%20overhead%20lighting%2C%20realistic%20paper%20texture%2C%20elegant%20minimal%20style&width=300&height=300&seq=thumb-pdf-contract-001&orientation=squarish',
  '와이어프레임.pdf':
    'https://readdy.ai/api/search-image?query=Modern%20UI%20wireframe%20document%20preview%2C%20clean%20white%20page%20with%20grayscale%20interface%20mockups%2C%20rectangular%20layout%20placeholders%2C%20simple%20geometric%20shapes%20representing%20website%20sections%2C%20minimal%20UX%20design%20sketches%2C%20professional%20wireframe%20style%2C%20organized%20grid%20layout%2C%20soft%20shadows&width=300&height=300&seq=thumb-pdf-wireframe-001&orientation=squarish',
};

export default function FileThumbnail({ item, size = 'md', className = '' }: FileThumbnailProps) {
  const isImage = item.mimeType?.startsWith('image/');
  const isPdf = item.mimeType === 'application/pdf';
  const dims = size === 'sm' ? 'w-8 h-8' : 'w-14 h-14 md:w-16 md:h-16';
  const rounded = size === 'sm' ? 'rounded-lg' : 'rounded-xl';
  const bgClass = 'bg-background-200';

  if (isImage) {
    const previewSrc = item.path
      ? getPreviewUrl(item.path)
      : mockImagePreviews[item.name] || null;

    if (previewSrc) {
      return (
        <span className={`${dims} flex items-center justify-center ${rounded} overflow-hidden ${bgClass} relative flex-shrink-0 ${className}`}>
          <img
            src={previewSrc}
            alt={item.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </span>
      );
    }
  }

  // PDF thumbnail preview
  if (isPdf) {
    const previewSrc = item.path
      ? getPreviewUrl(item.path)
      : mockPdfPreviews[item.name] || null;

    if (previewSrc) {
      // For server mode, PDFs use the actual preview URL — browser will render PDF thumbnail
      // For mock mode, we use generated SD images as document previews
      const isServerMode = !!item.path;
      return (
        <span className={`${dims} flex items-center justify-center ${rounded} overflow-hidden ${bgClass} relative flex-shrink-0 ${className}`}>
          {isServerMode ? (
            <iframe
              src={`${previewSrc}#toolbar=0&navpanes=0&scrollbar=0`}
              className="w-full h-full pointer-events-none"
              title={item.name}
              loading="lazy"
            />
          ) : (
            <img
              src={previewSrc}
              alt={item.name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          )}
        </span>
      );
    }
  }

  // Fallback: show icon for all non-image files (and images without preview)
  return (
    <span className={`${dims} flex items-center justify-center ${rounded} ${bgClass} relative flex-shrink-0 ${className}`}>
      <i className={`${getFileIcon(item.mimeType)} ${size === 'sm' ? 'text-lg' : 'text-2xl md:text-3xl'} ${getFileColor(item.mimeType)}`}></i>
    </span>
  );
}